// Engine de áudio (WebAudio puro, sons sintetizados originais — zero assets
// externos/licenças, registrado em docs/07): buses master → sfx/wind/music.
import { WindAudio } from './wind';
import { MusicGen } from './music';

export class AudioEngine {
  ctx: AudioContext | null = null;
  master: GainNode | null = null;
  sfxBus: GainNode | null = null;
  musicBus: GainNode | null = null;
  wind = new WindAudio();
  music = new MusicGen();
  private vols = { master: 80, wind: 70, sfx: 100, music: 40 };

  /** Chamar em resposta a gesto do usuário. */
  init(): void {
    if (this.ctx) {
      void this.ctx.resume();
      return;
    }
    this.ctx = new AudioContext();
    this.master = this.ctx.createGain();
    this.master.connect(this.ctx.destination);
    this.sfxBus = this.ctx.createGain();
    this.sfxBus.connect(this.master);
    const windBus = this.ctx.createGain();
    windBus.connect(this.master);
    this.wind.start(this.ctx, windBus);
    this.musicBus = this.ctx.createGain();
    this.musicBus.connect(this.master);
    this.music.start(this.ctx, this.musicBus);
    this.applyVolumes();
  }

  /** Volumes 0..100, aplicados ao vivo (sliders das settings). */
  setVolumes(master: number, wind: number, sfx: number, music: number): void {
    this.vols = { master, wind, sfx, music };
    this.applyVolumes();
  }

  private applyVolumes(): void {
    if (this.master) this.master.gain.value = (this.vols.master / 100) * 0.9;
    if (this.sfxBus) this.sfxBus.gain.value = this.vols.sfx / 100;
    if (this.musicBus) this.musicBus.gain.value = (this.vols.music / 100) * 0.9;
    this.wind.volume = (this.vols.wind / 100) * 0.9;
  }

  private blip(freq: number, dur: number, vol: number, type: OscillatorType = 'sine', slideTo?: number): void {
    if (!this.ctx || !this.sfxBus) return;
    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (slideTo) osc.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.connect(g);
    g.connect(this.sfxBus);
    osc.start(t);
    osc.stop(t + dur);
  }

  playJump(): void { this.blip(220, 0.09, 0.12, 'triangle', 330); }
  playLand(force: number): void {
    // force = |vel.y| do pouso
    const v = Math.min(force / 800, 1);
    this.blip(120 - v * 40, 0.12 + v * 0.08, 0.1 + v * 0.2, 'triangle', 60);
  }
  playTeleport(): void { this.blip(600, 0.15, 0.15, 'sine', 150); }
  playPush(): void { this.blip(200, 0.25, 0.2, 'sawtooth', 700); }
  playRunStart(): void { this.blip(880, 0.07, 0.12, 'square'); }
  playStage(): void {
    this.blip(660, 0.08, 0.16, 'sine');
    setTimeout(() => this.blip(880, 0.1, 0.16, 'sine'), 80);
  }
  playFinish(medal: string | null): void {
    // arpejo; medalha melhor = mais notas
    const notes = medal === 'author' ? [523, 659, 784, 1047, 1319]
      : medal === 'gold' ? [523, 659, 784, 1047]
      : medal === 'silver' ? [523, 659, 784]
      : medal === 'bronze' ? [523, 659]
      : [440, 523];
    notes.forEach((f, i) => setTimeout(() => this.blip(f, 0.25, 0.2, 'triangle'), i * 110));
  }
}
